import React, { useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Loader2, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATES = [
    { id: 'diagnostic', label: 'Diagnóstico (HTML simple)' },
    { id: 'welcome', label: 'Bienvenida' },
    { id: 'grant_access', label: 'Acceso Premium otorgado' },
    { id: 'trial_day_3', label: 'Trial día 3' },
    { id: 'trial_day_5', label: 'Trial día 5' },
    { id: 'trial_day_7', label: 'Trial día 7' },
];

export default function EmailDiagnosticPanel() {
    const [to, setTo] = useState('acontistaeduca@gmail.com');
    const [template, setTemplate] = useState('diagnostic');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!to.includes('@')) return toast.error('Email inválido');
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post('/admin/email/test', { to: to.trim(), template });
            setResult(data);
            if (data.ok) toast.success('Email enviado a Resend');
            else toast.error('Resend rechazó el envío');
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="border-sky-200 bg-gradient-to-br from-sky-50/60 via-white to-white" data-testid="email-diagnostic-panel">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading text-base text-slate-900">
                    <Mail className="h-5 w-5 text-sky-700" />
                    Diagnóstico de correos (Resend)
                </CardTitle>
                <p className="text-xs text-slate-500">
                    Envía un correo de prueba para verificar la configuración. Útil para confirmar la verificación del dominio.
                </p>
            </CardHeader>
            <CardContent>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <Label className="text-xs">Destinatario</Label>
                        <Input
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder="docente@example.cl"
                            data-testid="diag-to-input"
                            disabled={loading}
                            className="mt-1"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label className="text-xs">Template</Label>
                        <select
                            value={template}
                            onChange={(e) => setTemplate(e.target.value)}
                            disabled={loading}
                            data-testid="diag-template-select"
                            className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        >
                            {TEMPLATES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <Button type="submit" disabled={loading} className="gap-2" data-testid="diag-submit-btn">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            Enviar correo de prueba
                        </Button>
                    </div>
                </form>

                {result?.ok && (
                    <div className="mt-5 rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="diag-result-ok">
                        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                            <CheckCircle2 className="h-4 w-4" />
                            Resend aceptó el envío
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="text-slate-600">Destinatario:</div>
                            <div className="font-mono text-slate-900">{result.to}</div>
                            <div className="text-slate-600">From:</div>
                            <div className="font-mono text-slate-900">{result.from}</div>
                            <div className="text-slate-600">Modo:</div>
                            <div className="text-slate-900">{result.modo}</div>
                            <div className="text-slate-600">Email ID:</div>
                            <div className="font-mono text-slate-700 truncate">{result.email_id}</div>
                        </div>
                        {result.advertencia_sandbox && (
                            <p className="text-xs text-amber-900 leading-relaxed bg-amber-100/60 p-3 rounded-lg mt-2">
                                ⚠️ {result.advertencia_sandbox}
                            </p>
                        )}
                    </div>
                )}
                {result && !result.ok && (
                    <div className="mt-5 rounded-xl border-2 border-rose-200 bg-rose-50 p-4 space-y-2" data-testid="diag-result-error">
                        <div className="flex items-center gap-2 text-sm font-semibold text-rose-900">
                            <AlertCircle className="h-4 w-4" />
                            Resend rechazó el envío
                        </div>
                        <p className="text-xs text-rose-900 leading-relaxed bg-white/60 p-3 rounded-lg">
                            <strong>Diagnóstico:</strong> {result.diagnostico}
                        </p>
                        {result.consejos && (
                            <ul className="list-disc pl-5 text-xs text-slate-700 space-y-1">
                                {result.consejos.map((c, i) => (<li key={i}>{c}</li>))}
                            </ul>
                        )}
                        {result.siguiente_paso && (
                            <p className="text-xs text-rose-900 italic">{result.siguiente_paso}</p>
                        )}
                    </div>
                )}

                <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-1.5">
                    <p className="font-semibold text-slate-900">📘 Cómo verificar tu dominio en Resend</p>
                    <ol className="list-decimal pl-5 space-y-1">
                        <li>Entra a <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-sky-700 inline-flex items-center gap-1">resend.com/domains <ExternalLink className="h-3 w-3" /></a> y haz click en <em>Add Domain</em>.</li>
                        <li>Ingresa <code className="bg-white px-1 rounded">portafoliodigitaldocente.cl</code>.</li>
                        <li>Copia los registros DNS (MX, SPF, DKIM, DMARC) que Resend genera.</li>
                        <li>Pégalos en el DNS de tu proveedor (Cloudflare / NIC.cl / Vercel).</li>
                        <li>Espera la verificación (5–60 min).</li>
                        <li>Cambia <code className="bg-white px-1 rounded">RESEND_FROM</code> en el backend de producción a <code className="bg-white px-1 rounded">no-reply@portafoliodigitaldocente.cl</code> y reinicia.</li>
                        <li>Vuelve aquí, envía un test a un email externo. Debe aparecer ✓ Sin advertencia sandbox.</li>
                    </ol>
                </div>
            </CardContent>
        </Card>
    );
}
